import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Crown, Users, CheckCircle2, XCircle, UserCheck, Filter, RotateCcw, Download, X, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";
import downloadBlob from "../../utils/downloadBlob";
import { exportPaidMembers } from "../../services/api";
import { fetchPaidTierReport, fetchPaidMembers, clearDrill } from "../../store/reportSlices/membersReportSlice";

const PER_PAGE = 25;

/* Request shape lives in one place */
const buildPayload = (f) => ({
    from_date: f.fromDate || null,
    to_date: f.toDate || null,
    program_id: f.programId ? Number(f.programId) : null,
    status: f.status || null,
});
const defaultFilters = () => ({ fromDate: "", toDate: "", programId: "", status: "" });

/* Static class maps so Tailwind can see every class name */
const STATUS = {
    active: { label: "Active", chip: "bg-green-50 text-green-700", bar: "bg-green-500", text: "text-green-600" },
    expired: { label: "Expired/Inactive", chip: "bg-slate-100 text-slate-600", bar: "bg-slate-400", text: "text-slate-600" },
    suspended: { label: "Suspended", chip: "bg-amber-50 text-amber-700", bar: "bg-amber-400", text: "text-amber-600" },
};
const CARD_ACCENT = {
    amber: "bg-amber-50 text-amber-600",
    green: "bg-green-50 text-green-600",
    slate: "bg-slate-100 text-slate-600",
    blue: "bg-blue-50 text-blue-600",
};
const num = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
const customerName = (r) => (r.name || `${r.fname || ""} ${r.lname || ""}`).trim() || "Customer";

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
    <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${CARD_ACCENT[accent]}`}>
            <Icon size={22} />
        </div>
        <div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-2xl font-bold text-gray-800 leading-tight">{num(value)}</p>
            {sub && <p className="text-xs text-gray-400">{sub}</p>}
        </div>
    </div>
);

/* A count that opens the member list; zero stays plain text */
const CountLink = ({ value, onClick, className = "" }) =>
    Number(value) > 0 ? (
        <button type="button" onClick={onClick} className={`font-semibold underline-offset-2 hover:underline ${className}`}>
            {num(value)}
        </button>
    ) : (
        <span className="text-gray-300">0</span>
    );

/* ───────────── Drill-down modal ───────────── */
const MembersModal = ({ drill, baseFilters, onClose }) => {
    const dispatch = useDispatch();
    const { rows, total, lastPage, loading, error } = useSelector((s) => s.membersReport.drill);
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);

    const filters = { ...buildPayload(baseFilters), program_id: drill.programId || buildPayload(baseFilters).program_id, status: drill.status || buildPayload(baseFilters).status };

    useEffect(() => {
        dispatch(fetchPaidMembers({ perPage: PER_PAGE, page, filters }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, page, drill]);

    useEffect(() => () => dispatch(clearDrill()), [dispatch]);

    const handleExport = async () => {
        setExporting(true);
        try {
            downloadBlob(await exportPaidMembers(filters), "paid-members.csv");
        } catch {
            toastr.error("Unable to export. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-5 py-4 border-b">
                    <div>
                        <h4 className="font-bold text-gray-800">{drill.programName || "All tiers"}</h4>
                        <p className="text-xs text-gray-500">
                            {drill.status ? STATUS[drill.status].label : "All statuses"} · {num(total)} purchase{total === 1 ? "" : "s"}
                        </p>
                    </div>
                    <button onClick={handleExport} disabled={exporting || !total} className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#f4a406] text-white text-sm hover:bg-[#d89005] disabled:opacity-50">
                        <Download size={15} /> {exporting ? "Exporting…" : "Export Excel"}
                    </button>
                    <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100" aria-label="Close"><X size={18} /></button>
                </div>

                <div className="overflow-auto p-5">
                    {error ? (
                        <div className="text-center text-red-600 text-sm py-10">{error}</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                                    <th className="py-2 pr-4">Customer</th>
                                    <th className="py-2 pr-4">Contact</th>
                                    <th className="py-2 pr-4">Tier</th>
                                    <th className="py-2 pr-4">Card No</th>
                                    <th className="py-2 pr-4">Purchased</th>
                                    <th className="py-2 pr-4">Valid till</th>
                                    <th className="py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={7}><ShimmerLoader /><ShimmerLoader /><ShimmerLoader /></td></tr>
                                )}
                                {!loading && rows.length === 0 && (
                                    <tr><td colSpan={7} className="text-center text-gray-500 py-10">No members found for this selection.</td></tr>
                                )}
                                {!loading && rows.map((r, i) => (
                                    <tr key={`${r.user_id}-${r.card_number}-${i}`} className="border-b last:border-0">
                                        <td className="py-2 pr-4 font-medium text-gray-800">{customerName(r)}</td>
                                        <td className="py-2 pr-4 text-gray-600">{r.email || "—"}<br /><span className="text-xs text-gray-400">{r.mobile}</span></td>
                                        <td className="py-2 pr-4">{r.program_name}</td>
                                        <td className="py-2 pr-4">{r.card_number || "—"}</td>
                                        <td className="py-2 pr-4">{fmtDate(r.created_at)}</td>
                                        <td className="py-2 pr-4">{fmtDate(r.deactivation_date)}</td>
                                        <td className="py-2">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS[r.membership_status]?.chip}`}>
                                                {STATUS[r.membership_status]?.label || r.membership_status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {lastPage > 1 && (
                    <div className="px-5 py-3 border-t">
                        <Pagination totalPages={lastPage} currentPage={page} setCurrentPage={setPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

/* ───────────── Page ───────────── */
export default function PaidMembersByTier() {
    useDocumentTitle("Paid Members by Tier | Reports");
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector((s) => s.membersReport.tier);

    const [showFilters, setShowFilters] = useState(false);
    const [draft, setDraft] = useState(defaultFilters());
    const [applied, setApplied] = useState(defaultFilters());
    const [drill, setDrill] = useState(null); // { programId, programName, status }
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        dispatch(fetchPaidTierReport(buildPayload(applied)));
    }, [dispatch, applied]);

    const handleDraft = (e) => setDraft((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleApply = (e) => {
        e.preventDefault();
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) {
            toastr.error("From date cannot be after To date");
            return;
        }
        setApplied({ ...draft });
    };

    const handleReset = () => {
        const fresh = defaultFilters();
        setDraft(fresh);
        setApplied(fresh);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            downloadBlob(await exportPaidMembers(buildPayload(applied)), "paid-members.csv");
        } catch {
            toastr.error("Unable to export. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const summary = data?.summary || { total: 0, active: 0, expired: 0, suspended: 0, unique_customers: 0 };
    const tiers = data?.tiers || [];
    const options = data?.tier_options || [];
    const maxTotal = Math.max(1, ...tiers.map((t) => t.total));
    const activeFilters = ["fromDate", "toDate", "programId", "status"].filter((k) => applied[k]).length;
    const open = (t, status = "") => setDrill({ programId: t?.program_id || "", programName: t?.program_name || "All tiers", status });

    return (
        <div className="w-full space-y-4">
            {/* Header */}
            <div className="rounded-xl p-4 text-white shadow-lg bg-gradient-to-r from-[#f4a406] to-[#d89005]">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center"><Crown size={22} /></div>
                    <div>
                        <h3 className="text-xl font-bold leading-tight">Paid Members by Tier</h3>
                        <p className="text-sm text-white/90">Paid memberships only · one purchase = one membership record</p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <button type="button" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors">
                            <Filter size={16} /> {showFilters ? "Hide filters" : "Filters"}
                            {activeFilters > 0 && (
                                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#d89005] text-xs font-bold inline-flex items-center justify-center">{activeFilters}</span>
                            )}
                        </button>
                        <button type="button" onClick={handleExport} disabled={exporting || loading || !summary.total}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#d89005] text-sm font-medium hover:bg-white/90 disabled:opacity-60">
                            <Download size={16} /> {exporting ? "Exporting…" : "Export Excel"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <form onSubmit={handleApply} className="bg-white rounded-xl shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase date from</label>
                            <input type="date" name="fromDate" value={draft.fromDate} onChange={handleDraft}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase date to</label>
                            <input type="date" name="toDate" value={draft.toDate} min={draft.fromDate || undefined} onChange={handleDraft}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Membership tier</label>
                            <select name="programId" value={draft.programId} onChange={handleDraft}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                                <option value="">All tiers</option>
                                {options.map((o) => <option key={o.program_id} value={o.program_id}>{o.program_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Membership status</label>
                            <select name="status" value={draft.status} onChange={handleDraft}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                                <option value="">All statuses</option>
                                <option value="active">Active</option>
                                <option value="expired">Expired/Inactive</option>
                                <option value="suspended">Suspended</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end mt-4 space-x-3">
                        <button type="button" onClick={handleReset} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                            <RotateCcw size={15} /> Reset
                        </button>
                        <button type="submit" className="px-4 py-2 bg-[#f4a406] text-white rounded-md hover:bg-[#d89005]">Apply Filters</button>
                    </div>
                </form>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-center gap-3 text-sm">
                    <AlertTriangle size={18} /> <span>{error}</span>
                    <button onClick={() => dispatch(fetchPaidTierReport(buildPayload(applied)))} className="ml-auto underline">Retry</button>
                </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Paid Members" value={summary.total} sub="purchases" accent="amber" />
                <StatCard icon={CheckCircle2} label="Active Memberships" value={summary.active} accent="green" />
                <StatCard icon={XCircle} label="Expired/Inactive Memberships" value={summary.expired} sub={summary.suspended > 0 ? `+ ${num(summary.suspended)} suspended` : undefined} accent="slate" />
                <StatCard icon={UserCheck} label="Unique Customers" value={summary.unique_customers} sub="renewals counted once" accent="blue" />
            </div>

            {/* Tier distribution */}
            <div className="bg-white rounded-xl shadow p-5">
                <div className="flex items-center gap-4 flex-wrap mb-4">
                    <h4 className="font-semibold text-gray-800">Distribution across tiers</h4>
                    <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
                        {Object.entries(STATUS).map(([k, v]) => (
                            <span key={k} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-full ${v.bar}`} />{v.label}</span>
                        ))}
                    </div>
                </div>
                {loading && !data ? (
                    <><ShimmerLoader /><ShimmerLoader /><ShimmerLoader /></>
                ) : tiers.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">No paid membership tiers found.</p>
                ) : (
                    <div className={`space-y-4 ${loading ? "opacity-60" : ""}`}>
                        {tiers.map((t) => {
                            const share = summary.total > 0 ? ((t.total / summary.total) * 100).toFixed(1) : "0.0";
                            return (
                                <div key={t.program_id}>
                                    <div className="flex items-baseline justify-between text-sm mb-1">
                                        <span className="font-medium text-gray-800">{t.program_name}</span>
                                        <span className="text-gray-500">{num(t.total)} · {share}%</span>
                                    </div>
                                    <div className="h-3 rounded-full bg-gray-100 overflow-hidden flex" style={{ width: `${Math.max((t.total / maxTotal) * 100, t.total ? 3 : 0)}%` }}>
                                        {["active", "expired", "suspended"].map((k) => t[k] > 0 && (
                                            <div key={k} className={STATUS[k].bar} style={{ width: `${(t[k] / t.total) * 100}%` }} title={`${STATUS[k].label}: ${t[k]}`} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Detail table */}
            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase text-gray-500 bg-gray-50">
                            <th className="px-4 py-3">Membership Tier</th>
                            <th className="px-4 py-3 text-right">Total Purchased</th>
                            <th className="px-4 py-3 text-right">Active</th>
                            <th className="px-4 py-3 text-right">Expired/Inactive</th>
                            <th className="px-4 py-3 text-right">Suspended</th>
                            <th className="px-4 py-3 text-right">Unique Customers</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !data && <tr><td colSpan={6}><ShimmerLoader /><ShimmerLoader /></td></tr>}
                        {!loading && tiers.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-gray-500 py-10">No data for the selected filters.</td></tr>
                        )}
                        {tiers.map((t) => (
                            <tr key={t.program_id} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-800">{t.program_name}</td>
                                <td className="px-4 py-3 text-right"><CountLink value={t.total} onClick={() => open(t)} className="text-gray-800" /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={t.active} onClick={() => open(t, "active")} className={STATUS.active.text} /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={t.expired} onClick={() => open(t, "expired")} className={STATUS.expired.text} /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={t.suspended} onClick={() => open(t, "suspended")} className={STATUS.suspended.text} /></td>
                                <td className="px-4 py-3 text-right text-gray-600">{num(t.unique_customers)}</td>
                            </tr>
                        ))}
                    </tbody>
                    {tiers.length > 0 && (
                        <tfoot>
                            <tr className="border-t-2 bg-gray-50 font-bold">
                                <td className="px-4 py-3">Total</td>
                                <td className="px-4 py-3 text-right"><CountLink value={summary.total} onClick={() => open(null)} className="text-gray-800" /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={summary.active} onClick={() => open(null, "active")} className={STATUS.active.text} /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={summary.expired} onClick={() => open(null, "expired")} className={STATUS.expired.text} /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={summary.suspended} onClick={() => open(null, "suspended")} className={STATUS.suspended.text} /></td>
                                <td className="px-4 py-3 text-right text-gray-700">{num(summary.unique_customers)}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {drill && <MembersModal drill={drill} baseFilters={applied} onClose={() => setDrill(null)} />}
        </div>
    );
}